import { List } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Box, Stack } from "@mui/material";
import { useFacilityProfile } from "@src/appV2/Facilities/api/useFacilityProfile";
import { useGetRatingAggregates } from "@src/appV2/redesign/Workplace/Review/api/useGetRatingAggregates";

import { useInHomeVisitModalsDataContext } from "../../ShiftDiscovery/useInHomeVisitModalsDataContext";
import { PageContent } from "../../WorkerShifts/components/PageContent";
import { RATING_AGGREGATE_EMPTY } from "../../Workplace/Review/constants";
import { InHomeLockBanner } from "../OpenVisits/InHomeLockBanner";
import { CaseNotesListItem } from "../VisitBottomSheet/CaseNotesListItem";
import { InHomeAgencyDetailsListItem } from "../VisitBottomSheet/InHomeAgencyDetailsListItem";
import { PatientLocationListItem } from "../VisitBottomSheet/PatientLocationListItem";
import { VisitBottomSheetSkeleton } from "../VisitBottomSheet/Skeleton";
import { VisitDetailsListItem } from "../VisitBottomSheet/VisitDetailsListItem";
import { VisitStatusAlertCard } from "./StatusAlertCard";

interface BookedVisitDetailsContentProps {
  lockedByWorkplaceId?: string;
}

export function BookedVisitDetailsContent(props: BookedVisitDetailsContentProps) {
  const { lockedByWorkplaceId } = props;
  const {
    visit,
    case: caseData,
    patient,
    isVisitLoading,
    isVisitError,
  } = useInHomeVisitModalsDataContext();
  const { data: facilityRatings = RATING_AGGREGATE_EMPTY, isLoading: isLoadingRatings } =
    useGetRatingAggregates(caseData?.attributes.workplaceId ?? "", {
      enabled: isDefined(caseData?.attributes.workplaceId),
    });

  const { data: facility, isLoading: isLoadingFacility } = useFacilityProfile(
    { workplaceId: caseData?.attributes.workplaceId ?? "" },
    {
      enabled: isDefined(caseData?.attributes.workplaceId),
    }
  );

  const isPageDataError = !isVisitLoading && isVisitError;
  const isShowSkeleton =
    isVisitLoading ||
    isLoadingRatings ||
    isLoadingFacility ||
    !isDefined(visit) ||
    !isDefined(caseData) ||
    !isDefined(patient) ||
    !isDefined(facilityRatings) ||
    !isDefined(facility);

  return (
    <PageContent>
      <Stack
        paddingTop={4}
        sx={{
          height: "100%",
        }}
      >
        {isDefined(lockedByWorkplaceId) && (
          <Box sx={{ marginBottom: 4 }}>
            <InHomeLockBanner lockedByWorkplaceId={lockedByWorkplaceId} />
          </Box>
        )}

        {isPageDataError && (
          <Alert sx={{ marginY: 4 }} severity="error">
            Something went wrong while loading this visit. Please try again.
          </Alert>
        )}

        {isShowSkeleton && <VisitBottomSheetSkeleton />}
        {!isShowSkeleton && (
          <List>
            <VisitStatusAlertCard />
            <VisitDetailsListItem visit={visit} />
            <CaseNotesListItem caseData={caseData} />
            <PatientLocationListItem
              patient={patient}
              workplaceId={caseData.attributes.workplaceId}
            />
            <InHomeAgencyDetailsListItem facilityRatings={facilityRatings} facility={facility} />
          </List>
        )}
      </Stack>
    </PageContent>
  );
}
